import { AdjacentType, ComparisonType, LogicalOperatorType, Traversal, TraversalToAdjacentType } from '@iotv/iotv-v3-types'
import { getChildEdgeTypeId } from '../classDependentTypes/EdgeTypes/edgeTypeMatrixFns'
import { BranchingQueryDefinition } from '../util/dynamodb/GraphQuery/BranchingQueryDefinition'
import { DEBUG_OVERRIDE, NOT_LAMBDA } from '../util/lambda/debug'

const debug = DEBUG_OVERRIDE || (NOT_LAMBDA && false)

type GetNestedStructureBranchingQueryInput = {
  /** An object type for which nested structures of self are supported eg Group, Region */
  groupObjectType: string
  /** A valid object type that may be contained in the grouping type  */
  containedObjectType: string
  /** the canonical traversal sense */
  traversal: Traversal
  /** whether to return the contained items in Supported Type groups, because care needs to be taken as very large results sets may be produced */
  includeSupportedTypeInstances: boolean
  /** whether to add a  prefix to query ids, to avoid duplication when used in composite functions, default true */
  addIdPrefixes?: boolean
  /** Sub queries to run on each supported type retrieved */
  finalSubQueries?: BranchingQueryDefinition[]
}

/** returns a query definition to traverse the grouper -> grouper and containedType nested data
 * This query does not deal with customer - group - customer - group structure types. For this use desendingGroupHierarchy file://./descendingGroupHierarchy.ts
 * Testing is by utility: file:///.//../../../..//Utilities/src/TestData/EntityGroupsAndEntities/index.ts
 * This is used in group visibility use cases where the users group and determines what groups are allowed via the allowsGroup edgeType
 *
 * Note, as the initial query must do Grouper -> Grouper and have subQueries added, the top level Grouper items will not have the Grouper -> SupportedType query run against it, this needs to (currently 2024 05) be done by callers
 */
export const getNestedStructureBranchingQuery = ({
  groupObjectType,
  containedObjectType,
  traversal,
  includeSupportedTypeInstances,
  addIdPrefixes = true,
  finalSubQueries,
}: GetNestedStructureBranchingQueryInput): BranchingQueryDefinition | null => {
  let queryDef: BranchingQueryDefinition | null = null
  const idPrefix: string = addIdPrefixes ? `${groupObjectType}-${containedObjectType}-${traversal}-` : ''
  const grouperAdjacentType = TraversalToAdjacentType[traversal]
  const grouperGrouperEdgeTypeId = getChildEdgeTypeId(groupObjectType, groupObjectType) // should always resovle to group_has_subGroup
  const grouperContainedTypeEdgeTypeId = getChildEdgeTypeId(groupObjectType, containedObjectType)
  if (grouperGrouperEdgeTypeId && grouperContainedTypeEdgeTypeId) {
    const groupToGroupTypeQuery: BranchingQueryDefinition = new BranchingQueryDefinition({
      id: `${idPrefix}groupToGroupDefiningTypeQuery`,
      path: [
        {
          objectTypeId: groupObjectType,
          adjacencyType: grouperAdjacentType,
          edgeTypeId: grouperGrouperEdgeTypeId,
          nodeFilters: [],
        },
      ],
      subQueries: finalSubQueries ?? [],
    })

    if (groupObjectType === 'Group') {
      groupToGroupTypeQuery.path.at(0)?.nodeFilters?.push({
        setOperation: LogicalOperatorType.AND,
        filters: [{ key: 'supportedType', predicate: ComparisonType.EQUALS, value: containedObjectType }],
      })
    }

    if (includeSupportedTypeInstances) {
      const groupToContainedTypeQuery: BranchingQueryDefinition = new BranchingQueryDefinition({
        id: `${idPrefix}groupToContainedTypeQuery`,
        path: [{ objectTypeId: containedObjectType, adjacencyType: AdjacentType.CHILD, edgeTypeId: grouperContainedTypeEdgeTypeId }],
        subQueries: [],
      })

      groupToGroupTypeQuery.addSubQuery(groupToContainedTypeQuery)
    }

    groupToGroupTypeQuery.addSubQuery(groupToGroupTypeQuery)
    queryDef = groupToGroupTypeQuery
  }
  return queryDef
}
